import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { saveAudio, loadAudio, deleteAudio } from "../utils/audioDB";

const SETTINGS_KEY = "audioSettings_v1";

const DEFAULTS = {
  mioVolume:      1.0,
  mioMuted:       false,
  timeoutVolume:  1.0,
  timeoutMuted:   false,
  // nombres de archivo custom (solo metadato, el blob va en IndexedDB)
  mioFileName:    null,
  timeoutFileName: null,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
}
function saveSettings(s) {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch {}
}

const AudioSettingsContext = createContext(null);

export function AudioSettingsProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);

  // Object URLs de sonidos custom (null = usar el predeterminado)
  const [mioCustomUrl,     setMioCustomUrl]     = useState(null);
  const [timeoutCustomUrl, setTimeoutCustomUrl] = useState(null);

  // Revocar Object URLs al desmontar para no perder memoria
  const mioUrlRef     = useRef(null);
  const timeoutUrlRef = useRef(null);

  // Cargar blobs de IndexedDB al montar
  useEffect(() => {
    (async () => {
      try {
        const mioBlob = await loadAudio("mio");
        if (mioBlob) {
          const url = URL.createObjectURL(new Blob([mioBlob], { type: "audio/mpeg" }));
          mioUrlRef.current = url;
          setMioCustomUrl(url);
        }
      } catch {}
      try {
        const timeoutBlob = await loadAudio("timeout");
        if (timeoutBlob) {
          const url = URL.createObjectURL(new Blob([timeoutBlob], { type: "audio/mpeg" }));
          timeoutUrlRef.current = url;
          setTimeoutCustomUrl(url);
        }
      } catch {}
    })();

    return () => {
      if (mioUrlRef.current)     URL.revokeObjectURL(mioUrlRef.current);
      if (timeoutUrlRef.current) URL.revokeObjectURL(timeoutUrlRef.current);
    };
  }, []);

  // Sincronizar settings entre pestañas
  useEffect(() => {
    const handler = (e) => { if (e.key === SETTINGS_KEY) setSettings(loadSettings()); };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const update = useCallback((patch) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  // Subir sonido custom
  const uploadSound = useCallback(async (type, file) => {
    const buf = await file.arrayBuffer();
    await saveAudio(type, buf);

    // Revocar URL anterior
    if (type === "mio" && mioUrlRef.current)         URL.revokeObjectURL(mioUrlRef.current);
    if (type === "timeout" && timeoutUrlRef.current)  URL.revokeObjectURL(timeoutUrlRef.current);

    const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));

    if (type === "mio") {
      mioUrlRef.current = url;
      setMioCustomUrl(url);
      update({ mioFileName: file.name });
    } else {
      timeoutUrlRef.current = url;
      setTimeoutCustomUrl(url);
      update({ timeoutFileName: file.name });
    }
  }, [update]);

  // Eliminar sonido custom → volver al predeterminado
  const removeSound = useCallback(async (type) => {
    await deleteAudio(type);
    if (type === "mio") {
      if (mioUrlRef.current) { URL.revokeObjectURL(mioUrlRef.current); mioUrlRef.current = null; }
      setMioCustomUrl(null);
      update({ mioFileName: null });
    } else {
      if (timeoutUrlRef.current) { URL.revokeObjectURL(timeoutUrlRef.current); timeoutUrlRef.current = null; }
      setTimeoutCustomUrl(null);
      update({ timeoutFileName: null });
    }
  }, [update]);

  const reset = useCallback(() => {
    saveSettings(DEFAULTS);
    setSettings({ ...DEFAULTS });
  }, []);

  return (
    <AudioSettingsContext.Provider value={{
      settings,
      update, reset,
      uploadSound, removeSound,
      mioCustomUrl,     // null → usar default
      timeoutCustomUrl, // null → usar default
    }}>
      {children}
    </AudioSettingsContext.Provider>
  );
}

export function useAudioSettings() {
  const ctx = useContext(AudioSettingsContext);
  if (!ctx) throw new Error("useAudioSettings debe usarse dentro de AudioSettingsProvider");
  return ctx;
}
